import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, ArrowRight, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useConsent } from "@/components/Consent";
import { ARTIST_NAME, IMG, LIVE_DATES, RELEASES, PRESS_QUOTES, PROJECT_LABELS, VIDEOS } from "@/data/content";
import {
  Reveal, ChapterLabel, MaskLines, StaggerTitle, CTALink, ParallaxImage, Tilt, Marquee,
  BookingSection, usePageTitle, ImageReveal, FakePlayer, Magnetic,
  CursorFollowImage, EASE,
} from "@/components/Shared";

/* ================= HERO — cinematic campaign opener ================= */
const Hero = () => {
  const { t, lp } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 260]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] overflow-hidden">
      {/* Photography */}
      <motion.div style={{ y, scale: imgScale }} className="absolute inset-0">
        <img fetchPriority="high" decoding="async" src={IMG.hero} alt={ARTIST_NAME} className="animate-hero-zoom h-full w-full object-cover object-[50%_18%]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/25 to-[#0B0B0C]/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0C]/60 via-transparent to-[#0B0B0C]/40" />
      </motion.div>

      {/* moving light */}
      <div className="animate-light-sweep pointer-events-none absolute inset-y-0 left-1/2 w-[40vw] bg-gradient-to-r from-transparent via-[#C5A059]/10 to-transparent" />

      {/* vertical meta */}
      <div className="vertical-text absolute right-6 top-1/2 hidden -translate-y-1/2 lg:block">
        <span className="font-mono text-[10px] uppercase tracking-[0.55em] text-[#A1A1AA]/50">
          {t.home.heroWords.join("  —  ")}
        </span>
      </div>

      {/* Type block */}
      <motion.div style={{ opacity: fade }} className="absolute inset-x-0 bottom-0 z-10 px-6 pb-16 sm:px-12 lg:px-20">
        <Reveal y={16}>
          <p className="mb-10 flex flex-wrap items-center gap-x-4 gap-y-2 font-sans text-[10px] font-medium uppercase tracking-[0.25em] text-gold sm:mb-10 sm:flex-nowrap sm:whitespace-nowrap sm:text-[11px] sm:tracking-[0.4em]">
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1, delay: 0.4, ease: EASE }}
              className="h-px w-14 origin-left bg-[#C5A059]"
            />
            {t.home.heroEyebrow}
          </p>
        </Reveal>

        <h1 data-testid="hero-title" className="relative">
          <span className="sr-only">{ARTIST_NAME}</span>
          <motion.img
            src="/images/logo-name.webp"
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            decoding="async"
            draggable={false}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, delay: 0.35, ease: EASE }}
            className="block w-[80vw] max-w-[820px] select-none drop-shadow-[0_12px_40px_rgba(0,0,0,0.6)] sm:w-[54vw] lg:w-[38vw]"
          />
        </h1>

        <div className="mt-6 flex flex-wrap items-end justify-between gap-8">
          <Reveal delay={0.9} y={14}>
            <p className="font-serif text-lg italic text-[#F9F8F6]/80 sm:text-2xl">{t.home.heroClaim}</p>
          </Reveal>
          <Reveal delay={1.05} y={14} className="flex flex-wrap items-center gap-5">
            <Link
              to={lp("/contact")}
              data-testid="hero-cta-booking-button"
              data-cursor="OPEN"
              className="group inline-flex items-center gap-3 pb-1 font-sans text-[11px] font-medium uppercase tracking-[0.3em] text-[#A1A1AA] transition-colors duration-300 hover:text-[#C5A059]"
            >
              Booking
              <span className="h-px w-8 bg-current transition-all duration-500 group-hover:w-14" />
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </motion.div>

      {/* scroll hint */}
      <div className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex">
        <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#71717A]">{t.common.scroll}</span>
        <motion.span
          animate={{ scaleY: [1, 0.25, 1], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="h-12 w-px origin-top bg-[#C5A059]"
        />
      </div>
    </section>
  );
};

/* ================= 01 INTRO — overlapping editorial ================= */
const IntroSection = () => {
  const { t, lp } = useLang();
  return (
    <section className="relative px-6 py-28 sm:px-12 sm:py-40 lg:px-20">
      <span className="pointer-events-none absolute right-4 top-10 select-none font-serif text-[24vw] leading-none text-outline opacity-40 lg:text-[16vw]">
        {t.home.introNo}
      </span>
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <ImageReveal src="/images/artist-statement.webp" alt={ARTIST_NAME} className="aspect-[3/4]" />
        </div>
        <div className="relative z-10 lg:col-span-7 lg:-ml-24 lg:mt-40">
          <Reveal><ChapterLabel no={t.home.introNo} label={t.home.introLabel} /></Reveal>
          <div className="bg-[#0B0B0C]/60 py-2 backdrop-blur-sm lg:py-6">
            <MaskLines
              text={t.home.introTitle}
              className="font-serif text-[11vw] font-light leading-[0.95] text-[#F9F8F6] sm:text-6xl lg:text-[5.2vw]"
            />
          </div>
          <Reveal delay={0.25}>
            <p className="mt-8 max-w-md font-sans text-sm leading-loose text-[#A1A1AA] lg:ml-24">{t.home.introText}</p>
          </Reveal>
          <Reveal delay={0.35} className="mt-10 lg:ml-24">
            <CTALink to={lp("/biography")} testid="intro-biography-link" variant="outline">{t.home.introLink}</CTALink>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ================= 02 LIVE teaser — typographic dates ================= */
const LiveTeaser = () => {
  const { t, lp, lf } = useLang();
  const [preview, setPreview] = useState(null);
  const upcoming = LIVE_DATES.filter((d) => d.status === "upcoming").slice(0, 3);
  return (
    <section className="relative border-t border-white/5">
      <ParallaxImage src={IMG.audience} alt="" bw className="absolute inset-0 h-full" imgClass="opacity-30" speed={30} />
      <div className="relative z-10 px-6 py-28 sm:px-12 sm:py-36 lg:px-20">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <Reveal><ChapterLabel no={t.home.liveNo} label={t.home.liveLabel} /></Reveal>
            <MaskLines text={t.home.liveTitle} className="font-serif text-5xl font-light leading-[0.9] text-[#F9F8F6] sm:text-7xl lg:text-8xl" />
          </div>
          <Reveal delay={0.2}>
            <CTALink to={lp("/live")} testid="home-all-dates-link" variant="outline">{t.common.allDates}</CTALink>
          </Reveal>
        </div>

        <div className="mt-16" onMouseLeave={() => setPreview(null)}>
          {upcoming.map((d, i) => (
            <Reveal key={d.id} delay={i * 0.08}>
              <Link
                to={lp("/live")}
                data-cursor="VIEW"
                onMouseEnter={() => setPreview(d.img)}
                className="group relative grid grid-cols-[auto_1fr] items-end gap-x-6 gap-y-2 border-t border-white/10 py-8 last:border-b sm:grid-cols-12 sm:gap-8"
              >
                <div className="sm:col-span-3">
                  <span className="font-serif text-6xl font-light leading-none text-[#C5A059] transition-transform duration-500 group-hover:-translate-y-1 sm:text-8xl">
                    {d.day}
                  </span>
                  <span className="ml-3 font-mono text-[10px] tracking-[0.3em] text-[#71717A]">{d.month} '{d.year.slice(2)}</span>
                </div>
                <div className="sm:col-span-5">
                  <p className="font-serif text-3xl font-light text-[#F9F8F6] transition-all duration-500 group-hover:translate-x-2 group-hover:text-[#C5A059] sm:text-5xl">
                    {d.city}
                  </p>
                  <p className="mt-2 font-sans text-[11px] uppercase tracking-[0.25em] text-[#71717A]">{d.venue} · {lf(d.country)}</p>
                </div>
                <div className="col-span-2 flex items-center justify-between sm:col-span-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A059]/60">{lf(PROJECT_LABELS[d.project])}</span>
                  <ArrowRight className="h-5 w-5 text-[#71717A] transition-all duration-500 group-hover:translate-x-2 group-hover:text-[#C5A059]" />
                </div>
                <span className="absolute bottom-0 left-0 h-px w-0 bg-[#C5A059]/70 transition-all duration-700 ease-out group-hover:w-full" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
      <CursorFollowImage img={preview} />
    </section>
  );
};

/* ================= 03 BANDS — stacked fullscreen chapters ================= */
const BandsStack = () => {
  const { t, lp } = useLang();
  const items = t.bands.items;
  return (
    <section className="relative">
      <div className="px-6 pb-10 pt-28 sm:px-12 lg:px-20">
        <Reveal><ChapterLabel no={t.home.bandsNo} label={t.home.bandsLabel} /></Reveal>
        <MaskLines text={t.home.bandsTitle} className="font-serif text-5xl font-light leading-[0.9] text-[#F9F8F6] sm:text-7xl" />
      </div>
      <div className="relative">
        {items.map((b, i) => (
          <div key={b.key} className="sticky top-0 h-screen overflow-hidden">
            <Link
              to={lp(b.path)}
              data-testid={`band-card-${b.key === "band" ? "fullband" : b.key === "events" ? "eventmusic" : b.key}`}
              data-cursor="EXPLORE"
              className="group block h-full w-full"
            >
              <div className="absolute inset-0">
                <img src={IMG[b.img]} alt={b.name} loading="lazy" className="animate-kenburns h-full w-full object-cover" />
                <div className="absolute inset-0 bg-[#0B0B0C]/55 transition-colors duration-700 group-hover:bg-[#0B0B0C]/40" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-transparent to-[#0B0B0C]/60" />
              </div>
              <span className="absolute right-6 top-24 font-mono text-[10px] tracking-[0.4em] text-[#F9F8F6]/50 sm:right-12">
                {b.no} / 03
              </span>
              <div className="absolute inset-x-0 bottom-0 px-6 pb-16 sm:px-12 lg:px-20">
                <span className="font-mono text-xs tracking-[0.4em] text-[#C5A059]">{b.no}</span>
                <h3 className="mt-3 font-serif text-[13vw] font-light leading-[0.85] text-[#F9F8F6] transition-transform duration-700 group-hover:-translate-y-2 sm:text-[9vw] lg:text-[7vw]">
                  {b.name}
                </h3>
                <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
                  <p className="max-w-md font-sans text-sm leading-relaxed text-[#F9F8F6]/70">{b.desc}</p>
                  <span className="inline-flex items-center gap-3 font-sans text-[11px] font-medium uppercase tracking-[0.3em] text-[#C5A059]">
                    {t.common.explore}
                    <span className="h-px w-8 bg-[#C5A059] transition-all duration-500 group-hover:w-16" />
                    <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ================= 04 MUSIC — cover as art object ================= */
const MusicFeature = () => {
  const { t, lp, lf } = useLang();
  const featured = RELEASES.find((r) => r.featured);
  return (
    <section className="relative overflow-hidden border-t border-white/5 bg-[#101012] py-28 sm:py-40">
      <span className="pointer-events-none absolute left-1/2 top-16 -translate-x-1/2 select-none whitespace-nowrap font-serif text-[11vw] leading-none text-outline opacity-60">
        {featured.title}
      </span>
      <div className="relative z-10 grid items-center gap-14 px-6 sm:px-12 lg:grid-cols-12 lg:px-20">
        <div className="lg:col-span-5 lg:col-start-2">
          <Reveal>
            <Tilt max={10}>
              <div className="-rotate-2 shadow-[0_80px_120px_-40px_rgba(0,0,0,0.9)]">
                <img src={featured.cover} alt={featured.title} loading="lazy" className="aspect-square w-full object-cover" />
              </div>
            </Tilt>
          </Reveal>
        </div>
        <div className="lg:col-span-5 lg:col-start-8">
          <Reveal><ChapterLabel no={t.home.musicNo} label={t.home.musicLabel} /></Reveal>
          <Reveal delay={0.1}>
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#71717A]">
              {lf(featured.format)} · {featured.year}
            </p>
            <h3 className="mt-4 font-serif text-6xl font-light leading-none tracking-tight text-[#F9F8F6] sm:text-7xl">
              {featured.title}
            </h3>
          </Reveal>
          <Reveal delay={0.2}>
            <ul className="mt-10 max-w-sm">
              {featured.tracks.slice(0, 4).map((tr, i) => (
                <li key={tr} className="group flex cursor-default items-baseline gap-5 border-b border-white/5 py-3.5 transition-colors">
                  <span className="font-mono text-[10px] text-[#C5A059]/60">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-serif text-xl font-light text-[#A1A1AA] transition-all duration-400 group-hover:translate-x-2 group-hover:text-[#F9F8F6]">{tr}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.28} className="mt-9"><FakePlayer src={featured.preview} links={featured.links} /></Reveal>
          <Reveal delay={0.34} className="mt-9">
            <CTALink to={lp("/music")} testid="home-discover-music-link">{t.common.discoverMusic}</CTALink>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ================= 05 VIDEO teaser ================= */
const loadYouTubeApi = (cb) => {
  if (window.YT && window.YT.Player) return cb();
  const prev = window.onYouTubeIframeAPIReady;
  window.onYouTubeIframeAPIReady = () => { prev && prev(); cb(); };
  if (!document.getElementById("yt-iframe-api")) {
    const el = document.createElement("script");
    el.id = "yt-iframe-api";
    el.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(el);
  }
};

/* Thumbnail by default; the muted YouTube loop fades in only once it is really playing */
const VideoBackground = ({ video }) => {
  const ref = useRef(null);
  const { media } = useConsent();
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    if (!media) return undefined;
    let player;
    let cancelled = false;
    loadYouTubeApi(() => {
      if (cancelled || !ref.current) return;
      player = new window.YT.Player(ref.current, {
        width: "100%",
        height: "100%",
        host: "https://www.youtube-nocookie.com",
        videoId: video.youtubeId,
        playerVars: { autoplay: 1, mute: 1, controls: 0, loop: 1, playlist: video.youtubeId, rel: 0, playsinline: 1, modestbranding: 1, disablekb: 1, iv_load_policy: 3 },
        events: {
          onReady: (e) => { e.target.mute(); e.target.playVideo(); },
          onStateChange: (e) => { if (e.data === 1) setPlaying(true); },
        },
      });
    });
    return () => { cancelled = true; try { player && player.destroy(); } catch (_) {} };
  }, [video.youtubeId, media]);
  return (
    <>
      <img src={video.img} alt={video.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      {media && (
      <div
        data-testid="home-video-teaser-bg"
        data-playing={playing}
        className={`pointer-events-none absolute left-1/2 top-1/2 h-[56.25vw] min-h-full w-[177.78vh] min-w-full -translate-x-1/2 -translate-y-1/2 scale-[1.08] transition-opacity duration-1000 ${playing ? "opacity-100" : "opacity-0"}`}
      >
        <div ref={ref} className="h-full w-full" />
      </div>
      )}
    </>
  );
};

const VideoTeaser = () => {
  const { t, lp } = useLang();
  const [video] = useState(() => {
    const hd = VIDEOS.filter((v) => !["deqQCmO9aQw", "RRQT9JZMATg"].includes(v.youtubeId));
    return hd[Math.floor(Math.random() * hd.length)];
  });
  return (
    <section className="relative">
      <Link
        to={lp("/music/video")}
        data-testid="home-video-teaser"
        data-cursor="PLAY"
        className="group relative block h-[92vh] w-full overflow-hidden text-left"
      >
        <VideoBackground video={video} />
        <div className="absolute inset-0 bg-[#0B0B0C]/45 transition-colors duration-700 group-hover:bg-[#0B0B0C]/25" />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
          <ChapterLabel no={t.home.videoNo} label={t.home.videoLabel} />
          <StaggerTitle
            text={t.home.videoTitle}
            className="text-center font-serif text-[12vw] font-light leading-[0.9] text-[#F9F8F6] sm:text-[8vw]"
          />
          <Magnetic strength={0.4}>
            <motion.span
              whileHover={{ scale: 1.08 }}
              className="mt-12 flex h-24 w-24 items-center justify-center rounded-full border border-[#C5A059]/50 bg-[#0B0B0C]/40 backdrop-blur transition-colors duration-500 group-hover:bg-[#C5A059]"
            >
              <Play className="ml-1 h-7 w-7 text-[#C5A059] transition-colors duration-500 group-hover:text-[#0B0B0C]" />
            </motion.span>
          </Magnetic>
        </div>
      </Link>
    </section>
  );
};

/* ================= 06 PRESS voices ================= */
const PressVoices = () => {
  const { t, lf } = useLang();
  return (
    <section className="px-6 py-28 sm:px-12 sm:py-40 lg:px-20">
      <Reveal><ChapterLabel no={t.home.pressNo} label={t.home.pressLabel} /></Reveal>
      <div className="grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <MaskLines
            text={t.home.pressTitle}
            className="mb-10 font-serif text-5xl font-light text-[#F9F8F6] sm:text-6xl"
          />
          <Reveal delay={0.1}>
            <blockquote>
              <span className="font-serif text-8xl leading-none text-[#C5A059]">“</span>
              <p className="font-serif text-2xl font-light italic leading-snug text-[#F9F8F6] sm:text-4xl">
                {lf(PRESS_QUOTES[0].text)}
              </p>
              <cite className="mt-6 block font-mono text-[10px] uppercase not-italic tracking-[0.35em] text-[#71717A]">
                — {PRESS_QUOTES[0].source}
              </cite>
            </blockquote>
          </Reveal>
        </div>
        <div className="space-y-12 lg:col-span-4 lg:col-start-9 lg:mt-32">
          {PRESS_QUOTES.slice(1).map((q, i) => (
            <Reveal key={q.id} delay={0.15 + i * 0.1}>
              <blockquote className="border-l border-[#C5A059]/30 pl-6">
                <p className="font-serif text-lg font-light italic leading-relaxed text-[#F9F8F6]/80">{lf(q.text)}</p>
                <cite className="mt-4 block font-mono text-[10px] uppercase not-italic tracking-[0.3em] text-[#71717A]">— {q.source}</cite>
              </blockquote>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ================= PAGE ================= */
const HomePage = () => {
  const { t } = useLang();
  usePageTitle(`${ARTIST_NAME} — ${t.home.heroEyebrow}`);

  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee items={t.home.marquee} />
      <IntroSection />
      <BandsStack />
      <MusicFeature />
      <VideoTeaser />
      <PressVoices />
      <BookingSection />
    </div>
  );
};

export default HomePage;
